import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * The pill button every screen shares.
 *
 * Primary is ink, not blue — blue survives in this system only as link text
 * (CONTRACT.md §2.1). Anything a finger can hit is at least 44px tall, and it
 * is always a real <button> or <a>: a styled <div> gives up keyboard focus,
 * Enter/Space activation, and the accessible role all at once.
 *
 * `as="a"` picks the element; `to` routes through React Router (internal),
 * `href` renders a plain anchor (external — WhatsApp, docs). Both come out as
 * an <a>, so what the user sees and what assistive tech reads cannot drift.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'md' | 'sm';

interface CommonProps {
  variant?: ButtonVariant;
  /** `md` is the default across the app; `sm` slims the pill (less ink area). */
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    as?: 'button';
  };

type ButtonAsLink = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'> & {
    as: 'a';
    /** Internal route — rendered through React Router so the SPA does not reload. */
    to?: string;
    /** External destination. Ignored when `to` is set. */
    href?: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const BASE =
  'inline-flex items-center justify-center gap-2 min-h-[44px] rounded-pill font-extrabold ' +
  'no-underline text-center cursor-pointer transition-colors duration-150 ease-out ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

// Colour/border only. Padding + text size live in SIZES so a `size` override
// cannot collide with a variant's own padding in the class string.
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-ink text-white hover:bg-ink/90',
  secondary: 'border-2 border-ink text-ink hover:bg-surface-2',
  tertiary: 'border-2 border-muted text-ink hover:border-ink',
};

const SIZES: Record<ButtonSize, Record<ButtonVariant, string>> = {
  md: {
    primary: 'px-[30px] py-4 text-[17px]',
    secondary: 'px-[26px] py-[15px] text-[16px]',
    tertiary: 'px-[14px] py-2 text-[13px]',
  },
  sm: {
    primary: 'px-[22px] py-[11px] text-[15px]',
    secondary: 'px-[20px] py-[10px] text-[15px]',
    tertiary: 'px-[14px] py-2 text-[13px]',
  },
};

export function Button(props: ButtonProps) {
  const { as = 'button', variant = 'primary', size = 'md', fullWidth = false, className = '', children, ...rest } = props;
  const classes = `${BASE} ${VARIANTS[variant]} ${SIZES[size][variant]} ${fullWidth ? 'w-full' : ''} ${className}`;

  if (as === 'a') {
    const { to, href, ...anchor } = rest as { to?: string; href?: string } & AnchorHTMLAttributes<HTMLAnchorElement>;
    return to ? (
      <Link to={to} className={classes} {...anchor}>
        {children}
      </Link>
    ) : (
      <a href={href} className={classes} {...anchor}>
        {children}
      </a>
    );
  }

  const { type = 'button', ...button } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type={type} className={classes} {...button}>
      {children}
    </button>
  );
}

export default Button;
